import { Dispatch, SetStateAction } from 'react';
import { IntlShape } from 'react-intl';
import { toast } from 'react-toastify';
import { apiUrl } from 'router/url';
import {
  getEncodedQueryParamsString,
  QueryParameters,
} from 'utils/encoding/encodeQueryParamsString';
import { fetchWithAuthorization } from './authorizedFetch';

interface FetchTypes {
  setLoading?: Dispatch<SetStateAction<boolean>>;
  url: string;
  body?: any;
  intl?: IntlShape;
  /** INTL id. It returns string: `${label}.TOAST_ACCEPT` / `${label}.TOAST_DENIED` */
  label?: string;
  contentType?: 'json' | 'text' | 'formdata';
  signal?: AbortSignal;
  showErrorToast?: boolean;
  queryParams?: QueryParameters;
}

export interface FetchResponse<T = {}> {
  status: number;
  /** It contains boolean state whether the response was successful (status in the range 200-299) or not. */
  ok: boolean;
  data?: T;
}

interface FetchFunctionTypes extends FetchTypes {
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: HeadersInit;
}

const getDataType = (
  data: string,
  contentType?: 'json' | 'text' | 'formdata',
) => {
  if (contentType === 'text') {
    return data;
  }
  if (!data) {
    return {};
  }
  try {
    return JSON.parse(data);
  } catch {
    return {};
  }
};

const getErrorMessage = (
  response: Response,
  data: any,
  intl?: IntlShape,
  label?: string,
) =>
  data?.errorMessage ||
  (intl && label
    ? intl.formatMessage({ id: `${label}.TOAST_DENIED` })
    : `${response.status} ${response.statusText}`);

export const getFetch = async ({
  setLoading = () => {
    //intentional empty function
  },
  url = '',
  contentType = 'json',
  signal,
  showErrorToast = true,
  queryParams,
}: FetchTypes) => {
  setLoading(true);
  try {
    url += getEncodedQueryParamsString(queryParams);

    const response = await fetchWithAuthorization(apiUrl + url, {
      signal,
      headers: {
        'content-type':
          contentType === 'json' ? 'text/plain;charset=UTF-8' : 'charset=UTF-8',
      },
    });
    const data = getDataType(await response.text(), contentType);

    if (response.ok) {
      return data;
    }

    if (showErrorToast) {
      toast.error(getErrorMessage(response, data));
    }
  } catch (error) {
    if ((error as Error).name !== 'AbortError' && showErrorToast) {
      toast.error((error as Error).message);
    }
  } finally {
    setLoading(false);
  }
};

const fetchFunction = async ({
  setLoading = () => {
    // intentional empty function
  },
  url,
  method,
  body,
  headers,
  intl,
  label,
  contentType,
  showErrorToast = true,
}: FetchFunctionTypes) => {
  setLoading(true);
  try {
    const response = await fetchWithAuthorization(apiUrl + url, {
      headers,
      method,
      body,
    });
    const data = getDataType(await response.text(), contentType);
    const ok = response.ok;

    if (ok && intl && label) {
      toast.success(intl.formatMessage({ id: `${label}.TOAST_ACCEPT` }));
    } else if (!ok && showErrorToast) {
      toast.error(getErrorMessage(response, data, intl, label));
    }

    return {
      status: response.status,
      ok,
      data,
    };
  } catch (error) {
    if ((error as Error).name !== 'AbortError' && showErrorToast) {
      toast.error(
        intl?.formatMessage({ id: `TOAST_UNDEFINED_ERROR` }) ||
          (error as Error).message,
      );
    }
    return { status: 0, ok: false };
  } finally {
    setLoading(false);
  }
};

export const postFetch = async <T = {}>({
  setLoading = () => {
    // intentional empty function
  },
  url = '',
  body,
  intl,
  label,
  contentType,
  showErrorToast,
}: FetchTypes): Promise<FetchResponse<T>> =>
  fetchFunction({
    setLoading,
    intl,
    label,
    url,
    method: 'POST',
    body: contentType === 'formdata' ? body : JSON.stringify(body),
    contentType,
    showErrorToast,
    headers: {
      ...(contentType !== 'formdata' && {
        'content-type': 'application/json; charset=utf-8',
      }),
    },
  });

export const putFetch = async <T = {}>({
  setLoading = () => {
    // intentional empty function
  },
  url = '',
  body,
  intl,
  label,
  showErrorToast,
}: FetchTypes): Promise<FetchResponse<T>> =>
  fetchFunction({
    setLoading,
    intl,
    label,
    showErrorToast,
    url,
    method: 'PUT',
    body: JSON.stringify(body),
    headers: { 'content-type': 'application/json; charset=utf-8' },
  });

export const patchFetch = async <T = {}>({
  setLoading = () => {
    // intentional empty function
  },
  url = '',
  body,
  intl,
  label,
  showErrorToast,
}: FetchTypes): Promise<FetchResponse<T>> =>
  fetchFunction({
    setLoading,
    intl,
    label,
    showErrorToast,
    url,
    method: 'PATCH',
    body: JSON.stringify(body),
    headers: { 'content-type': 'application/json' },
  });

export const deleteFetch = async <T = {}>({
  setLoading = () => {
    // intentional empty function
  },
  url = '',
  intl,
  label,
  showErrorToast,
}: FetchTypes): Promise<FetchResponse<T>> =>
  fetchFunction({
    setLoading,
    intl,
    label,
    showErrorToast,
    url,
    method: 'DELETE',
  });
